import queryString from 'query-string';
import cookie from 'js-cookie';
import dayjs from 'dayjs';

import { UTM_LABELS_COOKIE, utmLabelsKey } from '@constants';

interface UTMLabels {
  utmSource: string,
  utmMedium: string,
  utmCampaign: string,
}

interface UTMLLabelsWithTimestamp extends UTMLabels {
  timestamp: number,
}

const UTM_LABELS_MAX_LIFETIME = 30; // days
const MAX_UTM_LABELS_LIMIT = 100;
const GOOGLE_REGEX = /^(https?:\/\/)?(www\.)?google\.com\/?/;

const parseUTMLabelsFromUrl = (): UTMLabels | null => {
  const queries = queryString.parse(window.location.search);

  const utmSource = queries?.utm_source;
  const utmMedium = queries?.utm_medium;
  const utmCampaign = queries?.utm_campaign;

  if (utmSource && utmMedium && utmCampaign) {
    return {
      utmSource,
      utmMedium,
      utmCampaign,
    };
  }

  return null;
};

const parseUtmLabelsFromReferrer = (): UTMLabels | null => {
  if (GOOGLE_REGEX.test(document.referrer)) {
    return {
      utmSource: 'organic',
      utmMedium: 'organic',
      utmCampaign: 'organic',
    };
  }

  return null;
};

const parseDirectUtmLabels = (): UTMLabels => ({
  utmSource: 'direct',
  utmMedium: 'direct',
  utmCampaign: 'direct',
});

const parseAllUTMLabels = () => {
  const utmLabelsFromUrl = parseUTMLabelsFromUrl();
  const utmLabelsFromReferrer = parseUtmLabelsFromReferrer();
  const directUtmLabels = parseDirectUtmLabels();

  return utmLabelsFromUrl || utmLabelsFromReferrer || directUtmLabels;
};

const filterUtmLabelsBytimestamp = (utmLabels: UTMLLabelsWithTimestamp[]) => {
  return utmLabels
    .filter((labels) => dayjs().diff(labels.timestamp, 'day', true) <= UTM_LABELS_MAX_LIFETIME)
    .slice(-MAX_UTM_LABELS_LIMIT);
};

const getUTMLabels = () => {
  const utmLabels = cookie.get(UTM_LABELS_COOKIE);
  const allUtmLabels = localStorage.getItem(utmLabelsKey) || '[]';
  const allFilteredUtmLabels = filterUtmLabelsBytimestamp(JSON.parse(allUtmLabels) as UTMLLabelsWithTimestamp[]);

  return {
    lastUtmLabels: utmLabels ? JSON.parse(utmLabels) as UTMLabels : null,
    allUtmLabelsWithTimestamp: allFilteredUtmLabels,
    allUtmLabels: allFilteredUtmLabels.map(({
      utmSource,
      utmMedium,
      utmCampaign,
    }) => ({
      utmSource,
      utmMedium,
      utmCampaign,
    })),
  };
};

const saveUTMLabels = (utmLabels: UTMLabels) => {
  cookie.set(
    UTM_LABELS_COOKIE,
    JSON.stringify(utmLabels),
    {
      expires: UTM_LABELS_MAX_LIFETIME,
    },
  );
};

const saveAllUTMLabels = (utmLabels: UTMLabels) => {
  const isAlreadySaved = sessionStorage.getItem(utmLabelsKey);

  if (!isAlreadySaved) {
    const prevUtmLabels = getUTMLabels();
  
    const newAllUtmLabels = prevUtmLabels.allUtmLabelsWithTimestamp
      .concat([{
        ...utmLabels,
        timestamp: Date.now(),
      }]);
  
    localStorage.setItem(utmLabelsKey, JSON.stringify(filterUtmLabelsBytimestamp(newAllUtmLabels)));
    sessionStorage.setItem(utmLabelsKey, 'true');
  }
};

export const useUTMLabels = () => ({
  parseAllUTMLabels,
  parseUTMLabelsFromUrl,
  getUTMLabels,
  saveUTMLabels,
  saveAllUTMLabels,
});
